/** Updated student dashboard with real data. */
import { useEffect, useState } from "react";
import { Link, Route, Routes } from "react-router-dom";
import {
  BookOpen, Compass, Heart, ShoppingBag, Calendar, Award, Bell, User as UserIcon,
  Library, FlaskConical, Settings as Cog, Activity, CheckCircle2, ArrowRight,
} from "lucide-react";
import {
  Eyebrow, PageHeader, EmptyState, Card, CardBody, Badge, LoadingState, Button, ArtCard, PriceBlock,
} from "../../components/ui";
import { DashboardLayout } from "./DashboardLayout";
import { useAuth } from "../auth/AuthContext";
import { api } from "../../lib/api";

const STUDENT_NAV = [
  { label: "Learning", items: [
    { to: "/dashboard", label: "Overview", Icon: Activity, exact: true },
    { to: "/dashboard/courses", label: "My Courses", Icon: BookOpen },
    { to: "/dashboard/certificates", label: "Certificates", Icon: Award },
    { to: "/dashboard/library", label: "Library", Icon: Library },
    { to: "/dashboard/research", label: "Research", Icon: FlaskConical },
  ]},
  { label: "Activity", items: [
    { to: "/dashboard/orders", label: "Orders", Icon: ShoppingBag },
    { to: "/dashboard/bookings", label: "Bookings", Icon: Calendar },
    { to: "/dashboard/wishlist", label: "Wishlist", Icon: Heart },
    { to: "/dashboard/notifications", label: "Notifications", Icon: Bell },
  ]},
  { label: "Account", items: [
    { to: "/dashboard/profile", label: "Profile", Icon: UserIcon },
    { to: "/dashboard/settings", label: "Settings", Icon: Cog },
  ]},
];

function useDashboard() {
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/dashboard/me").then((r) => setData(r.data)).catch(() => setData(false)); }, []);
  return data;
}

function Stat({ label, value }) {
  return (
    <Card variant="elevated"><CardBody>
      <div className="font-mono text-[11px] uppercase tracking-[0.16em] text-text-dim">{label}</div>
      <div className="font-display text-[44px] tracking-[-0.01em] mt-1">{value}</div>
    </CardBody></Card>
  );
}

function Overview() {
  const { user } = useAuth();
  const data = useDashboard();
  if (!data) return <LoadingState />;
  const s = data.stats;
  return (
    <>
      <Eyebrow>Welcome back</Eyebrow>
      <h1 className="font-display text-[clamp(30px,3.4vw,42px)] mt-3 leading-tight" data-testid="dashboard-greeting">
        Good to see you, {user?.name?.split(" ")[0] || "Seeker"}.
      </h1>
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mt-10">
        <Stat label="Courses" value={s.enrolled_courses} />
        <Stat label="Certificates" value={s.certificates} />
        <Stat label="Orders" value={s.orders} />
        <Stat label="Bookings" value={s.bookings} />
        <Stat label="Sadhanas" value={s.active_sadhanas} />
        <Stat label="Unread" value={s.unread_notifications} />
      </div>
      <div className="mt-12">
        <PageHeader eyebrow="Continue learning" title="Pick up where you left off" />
        {data.enrollments.length === 0 ? (
          <EmptyState icon={BookOpen} title="No courses yet" description="Browse our catalog to enroll in your first course."
            action={<Button asChild><Link to="/courses">Browse courses <ArrowRight size={14}/></Link></Button>} />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
            {data.enrollments.slice(0, 6).map((e, i) => (
              <Card key={e.id} interactive variant="elevated">
                <div className="aspect-[16/10] relative"><ArtCard seed={i % 5} motifSeed={(i+2)%4} /></div>
                <CardBody>
                  <h3 className="font-display text-[18px]">{e.course?.title || "Course"}</h3>
                  <div className="mt-4 h-1.5 rounded-full bg-surface-2 overflow-hidden">
                    <div className="h-full bg-mauve" style={{ width: `${e.progress_percent || 0}%` }} />
                  </div>
                  <div className="mt-2 flex items-center justify-between text-[12px] text-text-dim">
                    <span>{e.progress_percent || 0}% complete</span>
                    {e.completed_at && <Badge variant="success">Completed</Badge>}
                  </div>
                  {e.course?.slug && (
                    <Button asChild variant="ghost" size="sm" className="mt-4 w-full">
                      <Link to={`/courses/${e.course.slug}`}>Continue <ArrowRight size={12}/></Link>
                    </Button>
                  )}
                </CardBody>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function MyCourses() {
  const data = useDashboard();
  if (!data) return <LoadingState />;
  return (
    <>
      <PageHeader eyebrow="My learning" title="Courses" lede="All your enrolled courses." />
      {data.enrollments.length === 0 ? (
        <EmptyState icon={BookOpen} title="No enrollments yet" action={<Button asChild><Link to="/courses">Browse courses</Link></Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="my-courses-grid">
          {data.enrollments.map((e, i) => (
            <Card key={e.id} variant="elevated">
              <div className="aspect-[16/10] relative"><ArtCard seed={i % 5} motifSeed={(i+1)%4} /></div>
              <CardBody>
                <h3 className="font-display text-[18px]">{e.course?.title}</h3>
                <div className="mt-4 h-1.5 rounded-full bg-surface-2 overflow-hidden">
                  <div className="h-full bg-mauve" style={{ width: `${e.progress_percent}%` }} />
                </div>
                <div className="mt-2 text-[12px] text-text-dim">{e.progress_percent || 0}% · {e.completed_at ? "Completed" : "In progress"}</div>
                <div className="flex gap-2 mt-4">
                  {e.course?.slug && <Button asChild size="sm" className="flex-1"><Link to={`/courses/${e.course.slug}`}>Continue</Link></Button>}
                  {e.progress_percent < 100 && <Button size="sm" variant="ghost" onClick={async () => { await api.post(`/courses/${e.course_id}/progress?progress=${Math.min(100, (e.progress_percent || 0) + 25)}`); window.location.reload(); }}>+25%</Button>}
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

function Certificates() {
  const data = useDashboard();
  if (!data) return <LoadingState />;
  const download = async (c) => {
    try {
      const res = await api.get(`/certificates/${c.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = `NVL-Certificate-${c.vid}.pdf`; a.click();
      URL.revokeObjectURL(url);
    } catch (e) { alert(e.response?.data?.detail || e.message); }
  };
  return (
    <>
      <PageHeader eyebrow="Achievements" title="Certificates" lede="Verified completions, with verification IDs." />
      {data.certificates.length === 0 ? (
        <EmptyState icon={Award} title="No certificates yet" description="Complete a course to earn your first certificate." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="certificates-grid">
          {data.certificates.map((c, i) => (
            <Card key={c.id} variant="elevated">
              <div className="relative aspect-[16/10] grid place-items-center"><ArtCard seed={i % 5} motifSeed={(i+3)%4} />
                <div className="absolute inset-0 grid place-items-center"><Award size={48} className="text-gold/80" /></div>
              </div>
              <CardBody>
                <Badge variant="gold">Verified</Badge>
                <h3 className="font-display text-[20px] mt-3">{c.course_title}</h3>
                <div className="text-[11.5px] font-mono uppercase tracking-[0.16em] text-text-faint mt-2">VID · {c.vid}</div>
                <Button size="sm" className="mt-4" onClick={() => download(c)} data-testid={`download-cert-${c.id}`}>Download PDF</Button>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

function Orders() {
  const data = useDashboard();
  if (!data) return <LoadingState />;
  return (
    <>
      <PageHeader eyebrow="Commerce" title="Orders" />
      {data.orders.length === 0 ? <EmptyState icon={ShoppingBag} title="No orders yet" /> : (
        <div className="space-y-3" data-testid="orders-list">
          {data.orders.map((o) => (
            <Card key={o.id} variant="elevated"><CardBody className="flex items-center gap-5 p-5">
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-text-dim">{o.number}</div>
                <h3 className="font-display text-[18px] mt-1">{o.items?.length} item{o.items?.length === 1 ? "" : "s"}</h3>
                <div className="text-[12.5px] text-text-faint mt-1">{new Date(o.created_at).toLocaleString()}</div>
              </div>
              <Badge variant={o.status === "paid" ? "success" : "muted"}>{o.status}</Badge>
              <PriceBlock price={o.total_inr} size="sm" />
            </CardBody></Card>
          ))}
        </div>
      )}
    </>
  );
}

function Bookings() {
  const data = useDashboard();
  if (!data) return <LoadingState />;
  return (
    <>
      <PageHeader eyebrow="Schedule" title="Bookings" />
      {data.bookings.length === 0 ? <EmptyState icon={Calendar} title="No bookings yet" action={<Button asChild><Link to="/services">Book a session</Link></Button>} /> : (
        <div className="space-y-3" data-testid="bookings-list">
          {data.bookings.map((b) => (
            <Card key={b.id} variant="elevated"><CardBody className="flex items-center gap-5 p-5">
              <div className="flex-1 min-w-0">
                <h3 className="font-display text-[18px]">{b.service_title}</h3>
                <div className="text-[12.5px] text-text-dim mt-1">{new Date(b.scheduled_for).toLocaleString()} · {b.duration_minutes} min</div>
              </div>
              <Badge variant={b.status === "confirmed" ? "success" : b.status === "pending" ? "warning" : "muted"}>{b.status}</Badge>
            </CardBody></Card>
          ))}
        </div>
      )}
    </>
  );
}

function Profile() {
  const { user } = useAuth();
  return (
    <>
      <PageHeader eyebrow="Account" title="Profile" />
      <Card variant="elevated"><CardBody className="p-7">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div><div className="text-[11px] font-mono uppercase tracking-[0.16em] text-text-dim">Name</div><div className="text-[15px] mt-1">{user?.name || "—"}</div></div>
          <div><div className="text-[11px] font-mono uppercase tracking-[0.16em] text-text-dim">Email</div><div className="text-[15px] mt-1">{user?.email}</div></div>
          <div><div className="text-[11px] font-mono uppercase tracking-[0.16em] text-text-dim">Role</div><div className="text-[15px] mt-1 capitalize">{user?.role?.replace("_", " ")}</div></div>
          <div><div className="text-[11px] font-mono uppercase tracking-[0.16em] text-text-dim">Country</div><div className="text-[15px] mt-1">{user?.country || "—"}</div></div>
        </div>
      </CardBody></Card>
    </>
  );
}

function Wishlist() {
  const [data, setData] = useState(null);
  const load = () => api.get("/dashboard/wishlist").then((r) => setData(r.data)).catch(() => setData({ items: [] }));
  useEffect(() => { load(); }, []);
  const onRemove = async (kind, item_id) => { await api.post("/dashboard/wishlist/toggle", { kind, item_id }); load(); };
  if (!data) return <LoadingState />;
  return (
    <>
      <PageHeader eyebrow="Saved" title="Wishlist" lede="Items you've saved across the catalog." />
      {data.items.length === 0 ? (
        <EmptyState icon={Heart} title="Nothing saved yet" description="Tap the heart on any course, product or book to save it here."
          action={<Button asChild><Link to="/courses">Browse courses</Link></Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="wishlist-grid">
          {data.items.map((w, i) => (
            <Card key={`${w.kind}-${w.item.id}`} variant="elevated">
              <div className="aspect-[16/10] relative"><ArtCard seed={i % 5} motifSeed={(i+1)%4} /></div>
              <CardBody>
                <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-text-faint">{w.kind}</div>
                <h3 className="font-display text-[18px] mt-2">{w.item.title}</h3>
                <div className="flex gap-2 mt-4">
                  <Button asChild size="sm" className="flex-1"><Link to={`/${w.kind === "book" ? "library" : w.kind + "s"}/${w.item.slug}`}>View</Link></Button>
                  <Button size="sm" variant="ghost" onClick={() => onRemove(w.kind, w.item.id)} data-testid={`wishlist-remove-${w.item.slug}`}>Remove</Button>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

function Notifications() {
  const [data, setData] = useState(null);
  const load = () => api.get("/notifications").then((r) => setData(r.data)).catch(() => setData({ items: [], unread: 0 }));
  useEffect(() => { load(); }, []);
  const onRead = async (id) => { await api.post(`/notifications/${id}/read`); load(); };
  const onReadAll = async () => { await api.post(`/notifications/read-all`); load(); };
  if (!data) return <LoadingState />;
  return (
    <>
      <PageHeader eyebrow="Inbox" title="Notifications"
        actions={data.unread > 0 ? <Button size="sm" variant="ghost" onClick={onReadAll} data-testid="notif-read-all">Mark all read</Button> : null} />
      {data.items.length === 0 ? <EmptyState icon={Bell} title="You're all caught up" description="System, course, order and booking updates will appear here." /> : (
        <Card variant="elevated"><CardBody className="p-0"><ul className="divide-y divide-line" data-testid="notif-list">
          {data.items.map((n) => (
            <li key={n._id} className={`px-5 py-4 flex items-start gap-3 ${n.read ? "" : "bg-mauve/5"}`} data-testid={`notif-${n._id}`}>
              <span className={`w-2 h-2 rounded-full mt-2 flex-shrink-0 ${n.read ? "bg-text-faint" : "bg-mauve"}`} />
              <div className="flex-1 min-w-0">
                <div className="text-[14px] font-semibold">{n.title}</div>
                {n.body && <div className="text-[13px] text-text-dim mt-1">{n.body}</div>}
                <div className="flex items-center gap-3 mt-2">
                  <span className="text-[11px] text-text-faint font-mono uppercase tracking-[0.16em]">{n.kind}</span>
                  <span className="text-[11px] text-text-faint">{new Date(n.created_at).toLocaleString()}</span>
                  {n.link && <Link to={n.link} className="text-mauve text-[12px] hover:underline">Open →</Link>}
                </div>
              </div>
              {!n.read && <button onClick={() => onRead(n._id)} className="text-[11.5px] text-mauve hover:underline" data-testid={`notif-read-${n._id}`}>Mark read</button>}
            </li>
          ))}
        </ul></CardBody></Card>
      )}
    </>
  );
}

function SavedLibrary() {
  const data = useDashboard();
  if (!data) return <LoadingState />;
  const books = (data.recent_books || []);
  return (
    <>
      <PageHeader eyebrow="Reading" title="Library" lede="Books you've saved and read recently." />
      {books.length === 0 ? (
        <EmptyState icon={Library} title="No saved books yet" description="Browse the digital library to begin a personal reading shelf."
          action={<Button asChild><Link to="/library">Browse library</Link></Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {books.map((b, i) => (
            <Card key={b.id} variant="elevated">
              <div className="aspect-[16/10] relative"><ArtCard seed={i % 5} motifSeed={(i+2)%4} /></div>
              <CardBody>
                <h3 className="font-display text-[18px]">{b.title}</h3>
                <Button asChild size="sm" className="mt-4 w-full"><Link to={`/library/${b.slug}`}>Open</Link></Button>
              </CardBody>
            </Card>
          ))}
        </div>
      )}
    </>
  );
}

function SavedResearch() {
  const data = useDashboard();
  if (!data) return <LoadingState />;
  const papers = (data.recent_research || []);
  return (
    <>
      <PageHeader eyebrow="Scholarship" title="Research" lede="Papers and citations you've saved." />
      {papers.length === 0 ? (
        <EmptyState icon={FlaskConical} title="No papers saved yet" action={<Button asChild><Link to="/research">Browse research</Link></Button>} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          {papers.map((p) => (
            <Card key={p.id} variant="elevated"><CardBody className="p-5">
              <div className="text-[11px] font-mono uppercase tracking-[0.16em] text-text-faint">{p.journal || "NVL Research"}</div>
              <h3 className="font-display text-[18px] mt-2">{p.title}</h3>
              <p className="text-[13px] text-text-dim mt-2 line-clamp-3">{p.abstract || p.summary}</p>
              <Button asChild size="sm" variant="ghost" className="mt-4"><Link to={`/research/${p.slug}`}>Read paper →</Link></Button>
            </CardBody></Card>
          ))}
        </div>
      )}
    </>
  );
}

function Settings() {
  const { user, refresh } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", country: user?.country || "" });
  const [pw, setPw] = useState({ current: "", next: "" });
  const [msg, setMsg] = useState(null);
  const [pwMsg, setPwMsg] = useState(null);
  const onSave = async (e) => {
    e.preventDefault();
    try { await api.patch("/auth/me", form); setMsg("Saved."); if (refresh) refresh(); }
    catch (err) { setMsg(err.response?.data?.detail || err.message); }
  };
  const onChangePw = async (e) => {
    e.preventDefault();
    try { await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next }); setPwMsg("Password updated."); setPw({ current: "", next: "" }); }
    catch (err) { setPwMsg(err.response?.data?.detail || err.message); }
  };
  return (
    <>
      <PageHeader eyebrow="Account" title="Settings" lede="Manage your profile and password." />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card variant="elevated"><CardBody className="p-7">
          <Eyebrow>Profile</Eyebrow>
          <form onSubmit={onSave} className="mt-5 space-y-4">
            <div><label className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim block mb-1">Name</label>
              <input className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-mauve" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="settings-name" /></div>
            <div><label className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim block mb-1">Phone</label>
              <input className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-mauve" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="settings-phone" /></div>
            <div><label className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim block mb-1">Country</label>
              <input className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-mauve" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="settings-country" /></div>
            {msg && <div className="text-[13px] text-mauve">{msg}</div>}
            <Button type="submit" data-testid="settings-save">Save profile</Button>
          </form>
        </CardBody></Card>
        <Card variant="elevated"><CardBody className="p-7">
          <Eyebrow>Password</Eyebrow>
          <form onSubmit={onChangePw} className="mt-5 space-y-4">
            <div><label className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim block mb-1">Current password</label>
              <input type="password" className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-mauve" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="settings-current-pw" /></div>
            <div><label className="text-[12px] font-mono uppercase tracking-[0.14em] text-text-dim block mb-1">New password</label>
              <input type="password" className="w-full bg-surface border border-line-2 rounded-xl px-4 py-2.5 text-[14px] focus:outline-none focus:border-mauve" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="settings-new-pw" /></div>
            {pwMsg && <div className="text-[13px] text-mauve">{pwMsg}</div>}
            <Button type="submit" data-testid="settings-change-pw">Change password</Button>
          </form>
        </CardBody></Card>
      </div>
    </>
  );
}

function Stub({ title, description }) {
  return <><PageHeader eyebrow="Coming next" title={title} lede={description} /><EmptyState title="In development" description="This area is being assembled and will activate soon." /></>;
}

export function StudentDashboard() {
  return (
    <DashboardLayout nav={STUDENT_NAV} title="Student">
      <Routes>
        <Route index element={<Overview />} />
        <Route path="courses" element={<MyCourses />} />
        <Route path="certificates" element={<Certificates />} />
        <Route path="orders" element={<Orders />} />
        <Route path="bookings" element={<Bookings />} />
        <Route path="profile" element={<Profile />} />
        <Route path="library" element={<SavedLibrary />} />
        <Route path="research" element={<SavedResearch />} />
        <Route path="wishlist" element={<Wishlist />} />
        <Route path="notifications" element={<Notifications />} />
        <Route path="settings" element={<Settings />} />
        <Route path="*" element={<Stub title="Coming soon" description="" />} />
      </Routes>
    </DashboardLayout>
  );
}
